import React from 'react';
import { MultiSelect, Option } from '../../../components/ui/multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { CollapsibleFilterBar } from '../../project/components/dashboard/materials/tabs/Filters/CollapsibleFilterBar';
import type {
  EndPresenceOption,
  FlagFilter,
  PipeMarkFilter,
  PipeStatusFiltersState,
} from '../helpers/usePipeStatusFilters';

interface PipeStatusFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  filters: PipeStatusFiltersState;
  onFilterChange: (key: keyof PipeStatusFiltersState) => (value: string[]) => void;
  onEndPresenceChange: (value: EndPresenceOption[]) => void;
  onArchiveChange: (value: FlagFilter) => void;
  onPipeMarkChange: (value: PipeMarkFilter) => void;
  onDuplicatesChange: (value: FlagFilter) => void;
  uniquePipeIDs: string[];
  uniqueGroupIds: string[];
  /** Group ID filter only when Pipe Size = All. */
  showGroupIdFilter?: boolean;
  hasActiveFilters: boolean;
  filteredCount: number;
  onResetFilters: () => void;
}

const END_PRESENCE_OPTIONS: Option[] = [
  { value: 'both', label: 'Both ends' },
  { value: 'endAOnly', label: 'Only End A' },
  { value: 'endBOnly', label: 'Only End B' },
];

const ARCHIVE_OPTIONS: { value: FlagFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Archived' },
  { value: 'no', label: 'Active' },
];

const PIPE_MARK_OPTIONS: { value: PipeMarkFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'flange', label: 'Flange' },
  { value: 'anode', label: 'Anode' },
  { value: 'normal', label: 'Normal pipes' },
];

const DUPLICATES_OPTIONS: { value: FlagFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Duplicates only' },
  { value: 'no', label: 'Unique only' },
];

export const PipeStatusFilters: React.FC<PipeStatusFiltersProps> = ({
  searchQuery,
  onSearchChange,
  filters,
  onFilterChange,
  onEndPresenceChange,
  onArchiveChange,
  onPipeMarkChange,
  onDuplicatesChange,
  uniquePipeIDs,
  uniqueGroupIds,
  showGroupIdFilter = false,
  hasActiveFilters,
  filteredCount,
  onResetFilters,
}) => {
  const pipeIDOptions: Option[] = uniquePipeIDs.map((v) => ({ value: v, label: v }));
  const groupIdOptions: Option[] = uniqueGroupIds.map((v) => ({ value: v, label: v }));
  const activeFilterCount =
    filters.pipeID.length +
    (showGroupIdFilter ? filters.groupId.length : 0) +
    filters.endPresence.length +
    (filters.archive !== 'all' ? 1 : 0) +
    (filters.pipeMark !== 'all' ? 1 : 0) +
    (filters.duplicates !== 'all' ? 1 : 0);

  return (
    <CollapsibleFilterBar
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder={
        showGroupIdFilter ? 'Search by pipe ID or group…' : 'Search by pipe ID…'
      }
      activeFilterCount={hasActiveFilters ? Math.max(activeFilterCount, 1) : activeFilterCount}
      filteredCount={filteredCount}
      filteredCountLabel="pipes"
      onResetFilters={onResetFilters}
    >
      <MultiSelect
        options={pipeIDOptions}
        selected={filters.pipeID}
        onChange={onFilterChange('pipeID')}
        placeholder="Pipe ID"
        className="w-full"
      />
      {showGroupIdFilter ? (
        <MultiSelect
          options={groupIdOptions}
          selected={filters.groupId}
          onChange={onFilterChange('groupId')}
          placeholder="Group ID"
          className="w-full"
        />
      ) : null}
      <MultiSelect
        options={END_PRESENCE_OPTIONS}
        selected={filters.endPresence}
        onChange={(values) => onEndPresenceChange(values as EndPresenceOption[])}
        placeholder="End filter"
        className="w-full"
      />
      <Select value={filters.archive} onValueChange={(v) => onArchiveChange(v as FlagFilter)}>
        <SelectTrigger className="w-full h-9 text-[13px]">
          <SelectValue placeholder="Archive" />
        </SelectTrigger>
        <SelectContent>
          {ARCHIVE_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} className="text-[13px]">
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={filters.pipeMark} onValueChange={(v) => onPipeMarkChange(v as PipeMarkFilter)}>
        <SelectTrigger className="w-full h-9 text-[13px]">
          <SelectValue placeholder="Flange / Anode" />
        </SelectTrigger>
        <SelectContent>
          {PIPE_MARK_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} className="text-[13px]">
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={filters.duplicates}
        onValueChange={(v) => onDuplicatesChange(v as FlagFilter)}
      >
        <SelectTrigger className="w-full h-9 text-[13px]">
          <SelectValue placeholder="Duplicates" />
        </SelectTrigger>
        <SelectContent>
          {DUPLICATES_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} className="text-[13px]">
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </CollapsibleFilterBar>
  );
};
